/**
 * zoom.js — Pure calculation functions for digital zoom.
 *
 * Given a selection box drawn on a video container, computes the CSS
 * transform (scale + translate) to zoom into that region.
 *
 * All functions are pure (no DOM access) for easy testing.
 */

/**
 * Compute CSS transform to zoom into a selected rectangle.
 *
 * Uses transformOrigin at the selection center + scale only (no translate).
 * The browser scales around the origin point, naturally centering the selection.
 *
 * @param {Object} selection - { x1, y1, x2, y2 } in container-relative pixels
 * @param {Object} containerSize - { width, height } of the container element
 * @param {number} [maxScale=8] - Maximum allowed zoom level
 * @returns {Object} { scale, originX, originY } or null if selection too small
 */
export function computeZoomTransform(selection, containerSize, maxScale = 8) {
  const { x1, y1, x2, y2 } = selection;
  const { width, height } = containerSize;

  // Normalize so x1/y1 is always top-left
  const left = Math.min(x1, x2);
  const top = Math.min(y1, y2);
  const right = Math.max(x1, x2);
  const bottom = Math.max(y1, y2);

  const boxW = right - left;
  const boxH = bottom - top;

  // Minimum 10px box to avoid accidental zoom on click
  if (boxW < 10 || boxH < 10) return null;

  // Scale to fit the selected region into the full container
  const scaleX = width / boxW;
  const scaleY = height / boxH;
  // Use the smaller scale so the entire selection is visible
  const scale = Math.min(scaleX, scaleY, maxScale);

  // Center of the selection box — this becomes the transform origin
  const originX = Math.round(((left + right) / 2) * 100) / 100;
  const originY = Math.round(((top + bottom) / 2) * 100) / 100;

  return {
    scale: Math.round(scale * 100) / 100,
    originX,
    originY,
    // Top-left of selection in content coords — for scroll-pan positioning
    left: Math.round(left * 100) / 100,
    top: Math.round(top * 100) / 100
  };
}

/**
 * Build a CSS transform string from zoom parameters.
 *
 * @param {Object} zoom - { scale }
 * @returns {string} CSS transform value
 */
export function buildTransformString(zoom) {
  if (!zoom) return 'none';
  return `scale(${zoom.scale})`;
}

/**
 * Build a CSS transformOrigin string from zoom parameters.
 *
 * @param {Object} zoom - { originX, originY }
 * @returns {string} CSS transformOrigin value
 */
export function buildTransformOrigin(zoom) {
  if (!zoom) return 'center center';
  return `${zoom.originX}px ${zoom.originY}px`;
}

/**
 * Clamp a point to stay within container bounds.
 *
 * @param {number} x
 * @param {number} y
 * @param {Object} containerSize - { width, height }
 * @returns {Object} { x, y }
 */
export function clampToContainer(x, y, containerSize) {
  return {
    x: Math.max(0, Math.min(x, containerSize.width)),
    y: Math.max(0, Math.min(y, containerSize.height))
  };
}

/**
 * Get the distance between two touch points (for pinch-to-zoom).
 *
 * @param {Object} touch1 - { clientX, clientY }
 * @param {Object} touch2 - { clientX, clientY }
 * @returns {number} distance in pixels
 */
export function getTouchDistance(touch1, touch2) {
  const dx = touch1.clientX - touch2.clientX;
  const dy = touch1.clientY - touch2.clientY;
  return Math.sqrt(dx * dx + dy * dy);
}

/**
 * Get the midpoint between two touch points.
 *
 * @param {Object} touch1 - { clientX, clientY }
 * @param {Object} touch2 - { clientX, clientY }
 * @returns {Object} { x, y }
 */
export function getTouchMidpoint(touch1, touch2) {
  return {
    x: (touch1.clientX + touch2.clientX) / 2,
    y: (touch1.clientY + touch2.clientY) / 2
  };
}
